import { fetchApiGraphQL } from '@/utils/fetcher';
import type { PostalCode } from '../postalCodes';

/* The API calls these residence agents. Reads go through `me`, writes are
   mutations scoped to the logged-in user - see Troja Api
   Features/UserManagement/ResidenceAgents. */

export type Range = { minimum: number | null; maximum: number | null };

export interface SearchAgent {
    id: string;
    name: string | null;
    isActive: boolean;
    createdDate: string;
    rentPrice: Range | null;
    area: Range | null;
    rooms: Range | null;
    floor: Range | null;
    constructionYear: Range | null;
    postalCodes: { postalCodes: PostalCode[]; postalCodeAreas: PostalCodeArea[] } | null;
    properties: { properties: AgentProperty[] } | null;
}

export interface PostalCodeArea {
    id: string;
    name: string;
}

export interface AgentProperty {
    propertyNumber: string;
    propertyName: string | null;
}

/** The agent's filters. `updateResidenceAgent` replaces them all, so a filter left out is removed. */
export interface SearchAgentSettings {
    name?: string | null;
    rentPrice?: Range;
    area?: Range;
    rooms?: Range;
    floor?: Range;
    constructionYear?: Range;
    postalCodes?: { postalCodes: string[]; postalCodeAreaIds: string[] };
    properties?: { propertyNumbers: string[] };
}

const range = 'minimum maximum';
const AGENT_FIELDS = `
    id name isActive createdDate
    rentPrice { ${range} } area { ${range} } rooms { ${range} } floor { ${range} } constructionYear { ${range} }
    postalCodes { postalCodes { code city } postalCodeAreas { id name } }
    properties { properties { propertyNumber propertyName } }`;

const SETTINGS_VARIABLES =
    '$name: String, $rentPrice: DecimalRangeFilterInput, $area: DecimalRangeFilterInput, $rooms: IntegerRangeFilterInput, ' +
    '$floor: IntegerRangeFilterInput, $constructionYear: IntegerRangeFilterInput, $postalCodes: PostalCodeFilterInput, $properties: PropertyFilterInput';
const SETTINGS_ARGUMENTS =
    'name: $name, rentPrice: $rentPrice, area: $area, rooms: $rooms, floor: $floor, constructionYear: $constructionYear, postalCodes: $postalCodes, properties: $properties';

export async function getSearchAgents(): Promise<SearchAgent[]> {
    const result = await fetchApiGraphQL<{ data: { me: { residenceAgents: SearchAgent[] } } }>(
        `query { me { residenceAgents { ${AGENT_FIELDS} } } }`, {});
    return result.data.me.residenceAgents ?? [];
}

export async function createSearchAgent(settings: SearchAgentSettings): Promise<SearchAgent> {
    // Create takes the settings as top-level arguments; update takes them nested.
    const result = await fetchApiGraphQL<{ data: { createResidenceAgent: SearchAgent } }>(
        `mutation CreateResidenceAgent(${SETTINGS_VARIABLES}) { createResidenceAgent(${SETTINGS_ARGUMENTS}) { ${AGENT_FIELDS} } }`,
        { ...settings });
    return result.data.createResidenceAgent;
}

export async function updateSearchAgent(id: string, settings: SearchAgentSettings): Promise<SearchAgent> {
    const result = await fetchApiGraphQL<{ data: { updateResidenceAgent: SearchAgent } }>(
        `mutation UpdateResidenceAgent($id: ID!, $settings: ResidenceAgentSettingsInput!) { updateResidenceAgent(id: $id, settings: $settings) { ${AGENT_FIELDS} } }`,
        { id, settings });
    return result.data.updateResidenceAgent;
}

export async function setSearchAgentActive(id: string, isActive: boolean): Promise<SearchAgent> {
    const result = await fetchApiGraphQL<{ data: { setResidenceAgentActive: SearchAgent } }>(
        `mutation SetResidenceAgentActive($id: ID!, $isActive: Boolean!) { setResidenceAgentActive(id: $id, isActive: $isActive) { ${AGENT_FIELDS} } }`,
        { id, isActive });
    return result.data.setResidenceAgentActive;
}

export async function deleteSearchAgent(id: string): Promise<void> {
    await fetchApiGraphQL(`mutation DeleteResidenceAgent($id: ID!) { deleteResidenceAgent(id: $id) }`, { id });
}

/** The predefined postcode areas an agent can watch - the search's area choices too. */
export async function getPostalCodeAreas(): Promise<PostalCodeArea[]> {
    const result = await fetchApiGraphQL<{ data: { residenceAgentPostalCodeAreas: PostalCodeArea[] } }>(
        'query { residenceAgentPostalCodeAreas { id name } }', {});
    return result.data.residenceAgentPostalCodeAreas ?? [];
}

/** Every property an agent can watch, in one list. Public, like the other choice lists. */
export async function getAgentProperties(): Promise<AgentProperty[]> {
    const result = await fetchApiGraphQL<{ data: { residenceAgentProperties: AgentProperty[] } }>(
        'query { residenceAgentProperties { propertyNumber propertyName } }', {});
    return result.data.residenceAgentProperties ?? [];
}
